import { FC, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { FormContainerStateReturnType, useTrans } from "../../../hooks";
import { VStack, Input, useToast, Flex, Text, HStack } from "@chakra-ui/react";
import {
    InternshipScheduleSeat as Entity,
    InternshipScheduleSeatApi as EntityApi,
    InternshipApi,
    InternshipScheduleApi,
    School,
    SchoolApi,
} from "../../../apis";
import { UnprocessableErrorResponse } from "../../../services/ApiPlatform/models";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { schema } from "./schema";
import { AppFormContainer } from "../../../containers";
import {
    AppFormControl,
    AppSelect,
    AppSelectOption,
} from "../../../components";
import { useRecoilValue } from "recoil";
import { ActiveSchoolOrCompanyAtom } from "../../../atoms";
import { APPC } from "../../../config";

export const InternshipScheduleSeatForm: FC<{
    formContainerState: FormContainerStateReturnType;
}> = ({ formContainerState }) => {
    // hook
    const { t } = useTrans();
    const toast = useToast();
    const { id: internshipId, internshipScheduleId: scheduleId } = useParams<{
        id: string;
        internshipScheduleId: string;
    }>();
    const activeSchoolOrCompany = useRecoilValue(ActiveSchoolOrCompanyAtom);

    // state & const
    // const isEditMode: boolean = !!formContainerState.entityId;
    const [loading, setLoading] = useState(false);
    const [loadingAvailableSeats, setLoadingAvailableSeats] = useState(true);
    const [data, setData] = useState<Entity>(new Entity());
    const [loadingSchools, setLoadingSchools] = useState(true);
    const [schoolOptions, setSchoolOptions] = useState<AppSelectOption[]>([]);
    const [selectedSchool, setSelectedSchool] =
        useState<AppSelectOption | null>(null);
    const [totalSchools, setTotalSchools] = useState(0);
    const [SchoolPage, setSchoolPage] = useState(1);
    const [schoolSearch, setSchoolSearch] = useState("");
    const [availableSeats, setAvailableSeats] = useState(0);
    const [noOfAssignedSeats, setNoOfAssignedSeats] = useState(0);
    const PARTNERSHIPSTATUS = APPC.BE.Partnership.STATUS;

    const formSchema = useMemo(() => {
        const baseSchema = schema(t);
        const noOfSeatsValidation = yup.reach(
            baseSchema,
            "noOfSeats"
        ) as yup.NumberSchema;

        return baseSchema.shape({
            noOfSeats: noOfSeatsValidation
                .min(
                    noOfAssignedSeats,
                    t("ent.InternshipScheduleSeat:noOfSeats.val.minNumber", {
                        noOfAssignedSeats: noOfAssignedSeats,
                    })
                )
                .max(
                    availableSeats,
                    t("ent.InternshipScheduleSeat:noOfSeats.val.maxNumber")
                )
                .test(
                    "check-available-seats",
                    t("ent.InternshipScheduleSeat:noOfSeats.val.maxNumber"),
                    async (value) => {
                        if (!value) return false;
                        if (!scheduleId) return false;

                        try {
                            const { response } =
                                await InternshipScheduleApi.getAvailableSeats<{
                                    availableSeats: number;
                                }>(
                                    scheduleId,
                                    formContainerState.entityId || 0
                                );

                            if (response?.availableSeats) {
                                setAvailableSeats(response.availableSeats);
                                return value <= response.availableSeats;
                            }
                            return false;
                        } catch (error) {
                            return false;
                        }
                    }
                ),
        });
    }, [availableSeats, noOfAssignedSeats]);

    const {
        handleSubmit,
        register,
        reset,
        control,
        formState: { errors },
        setError,
    } = useForm<Entity>({
        resolver: yupResolver(formSchema),
        mode: "onSubmit",
    });

    // handler
    const onSubmitHandler = (formData: Entity) => {
        formData.internship = InternshipApi.toResourceIRI(internshipId);
        formData.internshipSchedule =
            InternshipScheduleApi.toResourceIRI(scheduleId);
        setLoading(true);
        EntityApi.createOrUpdate<Entity, Entity>(
            formContainerState.entityId,
            formData
        )
            .then(({ response, error, errorMessage }) => {
                if (error instanceof UnprocessableErrorResponse) {
                    const { violations } = error;
                    Object.keys(violations).map((key) => {
                        setError(key as never, {
                            type: "backend",
                            message: violations[key],
                        });
                    });
                } else if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    toast({
                        title: t("cmn:message.save.success"),
                        status: "success",
                    });
                    reset();
                    formContainerState.close();
                }
            })
            .finally(() => setLoading(false));
    };

    const loadSchoolOptions = () => {
        setLoadingSchools(true);
        const params = {
            [`order[name]`]: "asc",
            ["partnerships.company.id"]: activeSchoolOrCompany?.["@id"] ?? "",
            ["partnerships.status"]: PARTNERSHIPSTATUS.STATUS_APPROVED,
        };

        if (schoolSearch) {
            params["name"] = schoolSearch;
        }

        SchoolApi.getCollectionMyPartnership<School>(SchoolPage, params)
            .then(({ response }) => {
                if (response !== null) {
                    setTotalSchools(response.totalItems);
                    const newOptions =
                        response.items?.map((e) => ({
                            value: SchoolApi.toResourceIRI(e.id),
                            label: e.name,
                        })) || [];
                    SchoolPage === 1
                        ? setSchoolOptions(newOptions)
                        : setSchoolOptions((prevOptions) => [
                              ...prevOptions,
                              ...newOptions,
                          ]);
                }
            })
            .finally(() => {
                setLoadingSchools(false);
            });
    };

    const getAvailableSeats = () => {
        if (scheduleId) {
            setLoadingAvailableSeats(true);
            InternshipScheduleApi.getAvailableSeats<{
                availableSeats: number;
            }>(scheduleId, formContainerState.entityId || 0)
                .then(({ errorMessage, isNotFound, response }) => {
                    if (errorMessage) {
                        toast({
                            title: errorMessage,
                            status: "error",
                        });
                    } else if (isNotFound) {
                        toast({
                            title: t("cmn:message.entityNotFound"),
                            status: "error",
                        });
                    } else if (response !== null) {
                        setAvailableSeats(response?.availableSeats);
                    }
                })
                .finally(() => setLoadingAvailableSeats(false));
        }
    };

    // useEffect
    useEffect(() => {
        getAvailableSeats();
        if (!formContainerState.entityId) {
            reset();
            setData(new Entity());
            return;
        }
        setLoading(true);
        EntityApi.getItem<Entity>(formContainerState.entityId)
            .then(({ errorMessage, isNotFound, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (isNotFound) {
                    toast({
                        title: t("cmn:message.entityNotFound"),
                        status: "error",
                    });
                } else if (response !== null) {
                    reset();
                    setData(response);
                    setNoOfAssignedSeats(response.noOfAssignedSeats as number);
                    if (
                        response.school &&
                        typeof response.school !== "string"
                    ) {
                        setSelectedSchool({
                            value: SchoolApi.toResourceIRI(response.school.id),
                            label: response.school.name,
                        });
                    }
                }
            })
            .finally(() => setLoading(false));
    }, [formContainerState.entityId]);

    useEffect(() => {
        loadSchoolOptions();
    }, [SchoolPage, schoolSearch]);

    return (
        <AppFormContainer
            title={t("padm.InternshipScheduleSeatForm:text.pageTitle")}
            state={formContainerState}
            type={APPC.FORM_CONTAINER}
        >
            <form
                id="app-form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmitHandler)();
                }}
            >
                <VStack gap={5} alignItems={"stretch"}>
                    <AppFormControl
                        label={t("ent.InternshipScheduleSeat:school.label")}
                        isInvalid={!!errors.school}
                        message={errors.school?.message}
                        isLoading={loading}
                    >
                        <Controller
                            control={control}
                            name="school"
                            defaultValue={data?.school?.["@id"]}
                            render={({ field }) => (
                                <AppSelect<AppSelectOption>
                                    name={field.name}
                                    isClearable
                                    isLoading={loadingSchools}
                                    options={schoolOptions}
                                    value={selectedSchool}
                                    placeholder={t(
                                        "ent.InternshipScheduleSeat:school.placeholder"
                                    )}
                                    onChange={(e) => {
                                        field.onChange(e?.value || null);
                                        setSelectedSchool(e as AppSelectOption);
                                    }}
                                    onMenuScrollToBottom={() => {
                                        if (
                                            schoolOptions.length >= totalSchools
                                        ) {
                                            return;
                                        }
                                        setSchoolPage(
                                            (prevPage) => prevPage + 1
                                        );
                                    }}
                                    onInputChange={(inputValue: string) => {
                                        if (inputValue !== schoolSearch) {
                                            setSchoolSearch(inputValue);
                                            if (SchoolPage !== 1) {
                                                setSchoolPage(1);
                                            }
                                            setSchoolOptions([]);
                                        }
                                    }}
                                />
                            )}
                        />
                    </AppFormControl>
                    <AppFormControl
                        isInvalid={!!errors.noOfSeats}
                        message={errors.noOfSeats?.message}
                        isLoading={loading || loadingAvailableSeats}
                    >
                        <Flex direction={"column"}>
                            <HStack
                                w={"full"}
                                justifyContent={"space-between"}
                                mb={2}
                            >
                                <Text>
                                    {t(
                                        "ent.InternshipScheduleSeat:noOfSeats.label"
                                    )}
                                </Text>
                                <Text>
                                    {`${t("padm.InternshipScheduleSeatForm:text.availableSeats")}`}
                                    <Text
                                        as={"span"}
                                        color={"var(--chakra-colors-black)"}
                                        fontWeight={"600"}
                                    >
                                        {availableSeats > -1
                                            ? availableSeats
                                            : ""}
                                    </Text>
                                </Text>
                            </HStack>
                            <Input
                                type="number"
                                placeholder={t(
                                    "ent.InternshipScheduleSeat:noOfSeats.placeholder"
                                )}
                                {...register("noOfSeats")}
                                defaultValue={data.noOfSeats}
                            />
                        </Flex>
                    </AppFormControl>
                    {/* <AppFormControl
                        label={t("ent.InternshipScheduleSeat:isPublish.label")}
                        isLoading={loading}
                    >
                        <Switch
                            {...register("isPublish")}
                            defaultChecked={
                                data.isPublish 
                            }
                        />
                    </AppFormControl> */}
                </VStack>
            </form>
        </AppFormContainer>
    );
};
